import { useState, useEffect, useRef, useCallback } from 'react';
import { apiGet } from '@/lib/api';
interface LinkData {
  file: string;
  folderID: string;
  showContent?: string;
  page?: number;
  bboxes?: any;
  message?: string;
}
interface TooltipPosition {
  top: number | null;
  left: number;
  bottom: number | null;
  placement: string;
  linkRect: DOMRect | null;
}
/**
 * useDynamicLink Hook
 * 管理动态链接的数据获取、悬浮提示和点击行为
 */
export function useDynamicLink(href: string) {
  const [linkData, setLinkData] = useState<LinkData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [actualHref, setActualHref] = useState(href);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({
    top: null,
    left: 0,
    bottom: null,
    placement: 'top',
    linkRect: null,
  });
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Truncate text for display
   */
  const truncateText = useCallback((text: string, maxLength: number = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }, []);
  /**
   * Calculate tooltip position to prevent viewport overflow
   */
  const calculateTooltipPosition = useCallback(
    (element: HTMLElement): TooltipPosition => {
      const rect = element.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const tooltipWidth = 350;
      const tooltipHeight = 120;
      const offset = 8;
      // Default center alignment
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      let placement = 'top';
      let bottom = null;
      let top = null;
      // Check right boundary
      if (left + tooltipWidth > viewportWidth) {
        left = viewportWidth - tooltipWidth - 16;
      }
      // Check left boundary
      if (left < 16) {
        left = 16;
      }
      // Check if there's enough space above
      if (rect.top - tooltipHeight - offset < 16) {
        // Not enough space above, show below
        placement = 'bottom';
        top = rect.bottom + offset;
      } else {
        // Enough space above, use bottom positioning
        bottom = viewportHeight - rect.top + offset;
      }
      return { top, left, bottom, placement, linkRect: rect };
    },
    []
  );
  /**
   * Fetch link data from API
   */
  const fetchLinkData = useCallback(async () => {
    if (linkData || isLoading) return; // Avoid duplicate requests
    setIsLoading(true);
    try {
      // Replace example.com with current domain
      const apiUrl = href.replace('example.com', window.location.host);
      const response = await apiGet<any>(apiUrl);
      const data: LinkData = response?.data || response || {};

      let bbox = null;
      if (data.bboxes) {
        bbox = data.bboxes.map((it: any) => ({
          x1: it.x1,
          y1: it.y1,
          x2: it.x2,
          y2: it.y2,
          page: it.page || data.page,
        }));
        data.showContent =
          data.bboxes[0]?.content ||
          'There are no relevant information available to display. Please click to jump to the PDF for a detailed view.';
      } else {
        data.showContent = data.message || '';
      }
      setLinkData(data);
      // Build new href
      const newHref = `${window.location.origin}${encodeURIComponent(`/api/files/retrieve?filePath=${data.file}&folderID=${data.folderID}`)}${bbox ? `&bbox=${JSON.stringify(bbox)}` : ''}${data.page ? `#page=${data.page}` : ''}`;
      setActualHref(newHref);
    } catch (error) {
      console.error('Failed to fetch link data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [href, linkData, isLoading]);
  /**
   * Handle mouse enter
   */
  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      // Clear any existing timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      const position = calculateTooltipPosition(e.currentTarget);
      setTooltipPosition(position);
      setShowTooltip(true);
      fetchLinkData();
    },
    [calculateTooltipPosition, fetchLinkData]
  );
  /**
   * Handle mouse leave
   */
  const handleMouseLeave = useCallback(() => {
    // Add slight delay to avoid flickering
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, 100);
  }, []);
  /**
   * Clear timeout on mouse enter tooltip
   */
  const handleTooltipMouseEnter = useCallback(() => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
  }, []);
  /**
   * Handle link click
   */
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!linkData) {
        // Data not loaded yet, fail silently
        return;
      }
      // Use existing PDF open logic
      try {
        if ((window as any).openPDFInNewTab) {
          (window as any).openPDFInNewTab(actualHref, linkData.file);
        } else {
          // If openPDFInNewTab doesn't exist, open in new tab directly
          window.open(actualHref, '_blank');
        }
      } catch (error) {
        console.error('Error opening PDF:', error);
      }
    },
    [linkData, actualHref]
  );
  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);
  return {
    // State
    linkData,
    isLoading,
    showTooltip,
    actualHref,
    tooltipPosition,
    // Functions
    truncateText,
    handleMouseEnter,
    handleMouseLeave,
    handleTooltipMouseEnter,
    handleClick,
  };
}
